import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { useVehicleCategories } from "../lib/systemConfig";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Upload, X, ShoppingBag, CheckCircle2, Bike, Bike as Motorcycle, ChevronDown } from "lucide-react";
import { MOTORCYCLE_VEHICLE_TYPES, FUEL_OPTIONS, TRANSMISSION_OPTIONS, DRIVE_TYPES, CONDITION_OPTIONS, COLOR_BASES } from "../lib/listingConstants";

const REQUIRED_IMG = [
    { key: "img_front", label: "Frontansicht" },
    { key: "img_left", label: "Linke Seite" },
    { key: "img_right", label: "Rechte Seite" },
];
const OPTIONAL_IMG = [
    { key: "img_back", label: "Heckansicht" },
    { key: "img_dashboard", label: "Tacho" },
    { key: "img_damage", label: "Schäden" },
    { key: "img_papers", label: "Fahrzeugpapiere" },
];

export default function BuyRequestPage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { motorrad: sysMoto, fahrrad: sysBike } = useVehicleCategories();
    const [step, setStep] = useState(1);
    const [showMoreMoto, setShowMoreMoto] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState("");
    const [form, setForm] = useState({
        category: "motorcycle",
        brand: "", model: "", year: new Date().getFullYear() - 5,
        postal_code: "", rideable: true,
        contact_email: "", contact_name: "", contact_phone: "",
        desired_price: "", available_from: "", message: "",
        mileage_km: "", power_kw: "", power_hp: "",
        tuev_until: "", last_service: "", owners_count: "",
        accident_free: true, defects: "",
        vehicle_type: "", displacement: "", fuel: "", transmission: "", drive_type: "", condition: "", color_base: "",
        bike_type: "", frame_size: "", condition_grade: 3, battery_wh: "",
        img_front: "", img_left: "", img_right: "",
        img_back: "", img_dashboard: "", img_damage: "", img_papers: "",
    });

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    // Jun-2026 — System-weiter Admin-Schalter: deaktivierte Kategorien ausblenden.
    useEffect(() => {
        if (sysMoto && !sysBike && form.category !== "motorcycle") update("category", "motorcycle");
        else if (sysBike && !sysMoto && form.category !== "bicycle") update("category", "bicycle");
    }, [sysMoto, sysBike]); // eslint-disable-line react-hooks/exhaustive-deps

    const uploadImage = async (key, file) => {
        if (!file) return;
        setUploading(key);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            update(key, `${process.env.REACT_APP_BACKEND_URL}${data.url}`);
            toast.success("Bild hochgeladen");
        } catch {
            toast.error("Upload fehlgeschlagen");
        } finally {
            setUploading("");
        }
    };

    const canNextStep1 = form.category && form.brand && form.model && form.year && form.postal_code;
    const canNextStep2 = REQUIRED_IMG.every((r) => form[r.key]);
    const canSubmit = form.contact_email && form.contact_name;

    const submit = async () => {
        if (!canSubmit) {
            toast.error("Bitte Kontaktdaten ausfüllen");
            return;
        }
        setSubmitting(true);
        try {
            const payload = { ...form };
            // Cast numeric fields
            ["year", "mileage_km", "power_kw", "power_hp", "owners_count", "desired_price", "condition_grade", "battery_wh"].forEach((k) => {
                if (payload[k] === "" || payload[k] === null || payload[k] === undefined) {
                    delete payload[k];
                } else {
                    payload[k] = Number(payload[k]);
                }
            });
            // Drop empty strings → undefined
            Object.keys(payload).forEach((k) => {
                if (payload[k] === "") delete payload[k];
            });
            const { data } = await api.post("/buy-requests", payload);
            toast.success(t("buy_request.submit_success"));
            // Best-effort referral attribution
            try {
                const { attributeReferralIfPresent } = await import("../lib/referral");
                if (data?.id) await attributeReferralIfPresent(api, { reference_id: data.id, reference_type: "buy_request" });
            } catch { /* silent */ }
            navigate(`/ankauf/status/${data.id}?token=${data.verify_token}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Senden fehlgeschlagen");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-[#F8F9FA] min-h-screen pb-16" data-testid="buy-request-page">
            {/* Hero */}
            <div className="bg-[#111827] text-white">
                <div className="max-w-5xl mx-auto px-6 py-16">
                    <Badge className="bg-[#DC2626] text-white hover:bg-[#DC2626] rounded-sm uppercase tracking-widest text-[10px] font-bold mb-4">
                        {t("buy_request.hero_badge")}
                    </Badge>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-4">
                        {t("buy_request.hero_title_a")}<br />
                        <span className="text-[#FFCC00]">{t("buy_request.hero_title_b")}</span>
                    </h1>
                    <p className="text-lg text-gray-300 max-w-2xl">
                        {t("buy_request.hero_subtitle")}
                    </p>
                    <div className="flex flex-wrap gap-6 mt-8 text-sm">
                        <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#FFCC00]" /> {t("buy_request.feature_1")}</div>
                        <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#FFCC00]" /> {t("buy_request.feature_2")}</div>
                        <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#FFCC00]" /> {t("buy_request.feature_3")}</div>
                    </div>
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-6 -mt-8">
                {/* Stepper */}
                <div className="bg-white border border-gray-200 rounded-sm p-3 flex gap-2 mb-6 shadow-sm">
                    {[
                        { n: 1, t: t("buy_request.step_vehicle") },
                        { n: 2, t: t("buy_request.step_photos") },
                        { n: 3, t: t("buy_request.step_contact") },
                    ].map((s) => (
                        <div key={s.n} className={`flex-1 text-center py-2 px-3 rounded-sm text-xs font-bold uppercase tracking-wider ${step === s.n ? "bg-[#FFCC00] text-[#111827]" : step > s.n ? "bg-[#111827] text-white" : "bg-gray-100 text-gray-500"}`}>
                            {step > s.n && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                            {s.n}. {s.t}
                        </div>
                    ))}
                </div>

                {/* STEP 1 — Vehicle */}
                {step === 1 && (
                    <div className="bg-white border border-gray-200 rounded-sm p-6 space-y-4" data-testid="step-vehicle">
                        <h2 className="font-display text-2xl font-black">{t("buy_request.your_vehicle")}</h2>

                        <div className={`grid ${sysMoto && sysBike ? "grid-cols-2" : "grid-cols-1"} gap-3`}>
                            {[
                                { v: "motorcycle", t: t("buy_request.category_motorcycle"), Icon: Motorcycle, show: sysMoto },
                                { v: "bicycle", t: t("buy_request.category_bicycle"), Icon: Bike, show: sysBike },
                            ].filter((c) => c.show).map((c) => (
                                <button
                                    key={c.v}
                                    type="button"
                                    onClick={() => update("category", c.v)}
                                    className={`p-4 border-2 rounded-sm text-left transition ${form.category === c.v ? "border-[#FFCC00] bg-[#FFFBEB]" : "border-gray-200 hover:border-gray-400"}`}
                                    data-testid={`category-${c.v}`}
                                >
                                    <c.Icon className="w-6 h-6 mb-2" />
                                    <div className="font-bold">{c.t}</div>
                                </button>
                            ))}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Marke *</Label>
                                <Input value={form.brand} onChange={(e) => update("brand", e.target.value)} placeholder="z. B. BMW" className="rounded-sm mt-1" data-testid="input-brand" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Modell *</Label>
                                <Input value={form.model} onChange={(e) => update("model", e.target.value)} placeholder="z. B. R 1250 GS" className="rounded-sm mt-1" data-testid="input-model" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Erstzulassung *</Label>
                                <Input type="number" value={form.year} onChange={(e) => update("year", e.target.value)} className="rounded-sm mt-1 font-mono-num" data-testid="input-year" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">PLZ *</Label>
                                <Input value={form.postal_code} onChange={(e) => update("postal_code", e.target.value)} placeholder="80331" maxLength={10} className="rounded-sm mt-1 font-mono-num" data-testid="input-plz" />
                            </div>
                        </div>

                        <label className="flex items-center gap-2 cursor-pointer pt-2">
                            <input type="checkbox" checked={form.rideable} onChange={(e) => update("rideable", e.target.checked)} className="w-4 h-4 accent-[#FFCC00]" data-testid="input-rideable" />
                            <span className="text-sm font-bold">{t("buy_request.rideable")}</span>
                        </label>

                        {form.category === "motorcycle" ? (
                            <div className="pt-4 border-t border-gray-100 space-y-4">
                                <div className="text-xs font-bold uppercase tracking-wider text-gray-400" data-testid="moto-min-data-heading">Fahrzeugdaten (wie beim Inserieren)</div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Fahrzeugtyp</Label>
                                        <select value={form.vehicle_type} onChange={(e) => update("vehicle_type", e.target.value)} className="rounded-sm mt-1 w-full border border-gray-300 h-9 px-2 text-sm bg-white" data-testid="input-vehicle-type">
                                            <option value="">Bitte wählen …</option>
                                            {MOTORCYCLE_VEHICLE_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Kilometerstand</Label>
                                        <Input type="number" value={form.mileage_km} onChange={(e) => update("mileage_km", e.target.value)} placeholder="35000" className="rounded-sm mt-1 font-mono-num" data-testid="input-mileage" />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Hubraum (ccm)</Label>
                                        <Input type="number" value={form.displacement} onChange={(e) => update("displacement", e.target.value)} placeholder="1250" className="rounded-sm mt-1 font-mono-num" data-testid="input-displacement" />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Leistung (kW)</Label>
                                        <Input type="number" value={form.power_kw} onChange={(e) => update("power_kw", e.target.value)} placeholder="100" className="rounded-sm mt-1 font-mono-num" data-testid="input-power-kw" />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Kraftstoff</Label>
                                        <select value={form.fuel} onChange={(e) => update("fuel", e.target.value)} className="rounded-sm mt-1 w-full border border-gray-300 h-9 px-2 text-sm bg-white" data-testid="input-fuel">
                                            <option value="">Bitte wählen …</option>
                                            {FUEL_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Getriebe</Label>
                                        <select value={form.transmission} onChange={(e) => update("transmission", e.target.value)} className="rounded-sm mt-1 w-full border border-gray-300 h-9 px-2 text-sm bg-white" data-testid="input-transmission">
                                            <option value="">Bitte wählen …</option>
                                            {TRANSMISSION_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Antrieb</Label>
                                        <select value={form.drive_type} onChange={(e) => update("drive_type", e.target.value)} className="rounded-sm mt-1 w-full border border-gray-300 h-9 px-2 text-sm bg-white" data-testid="input-drive-type">
                                            <option value="">Bitte wählen …</option>
                                            {DRIVE_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Zustand</Label>
                                        <select value={form.condition} onChange={(e) => update("condition", e.target.value)} className="rounded-sm mt-1 w-full border border-gray-300 h-9 px-2 text-sm bg-white" data-testid="input-condition">
                                            <option value="">Bitte wählen …</option>
                                            {CONDITION_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Farbbasis</Label>
                                        <select value={form.color_base} onChange={(e) => update("color_base", e.target.value)} className="rounded-sm mt-1 w-full border border-gray-300 h-9 px-2 text-sm bg-white" data-testid="input-color-base">
                                            <option value="">Bitte wählen …</option>
                                            {COLOR_BASES.map((v) => <option key={v} value={v}>{v}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Anzahl Vorbesitzer</Label>
                                        <Input type="number" min="1" max="20" value={form.owners_count} onChange={(e) => update("owners_count", e.target.value)} placeholder="1" className="rounded-sm mt-1 font-mono-num" data-testid="input-owners" />
                                    </div>
                                </div>

                                <button type="button" onClick={() => setShowMoreMoto((v) => !v)} className="flex items-center gap-1.5 text-sm font-bold text-[#111827] hover:text-[#E6B800] transition-colors" data-testid="toggle-more-moto">
                                    <ChevronDown className={`w-4 h-4 transition-transform ${showMoreMoto ? "rotate-180" : ""}`} />
                                    {showMoreMoto ? "Weniger Angaben" : "Alle weiteren Daten erfassen (optional)"}
                                </button>

                                {showMoreMoto && (
                                    <div className="grid grid-cols-2 gap-3 pt-1" data-testid="moto-more-data">
                                        <div>
                                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">HU bis (MM/JJJJ)</Label>
                                            <Input value={form.tuev_until} onChange={(e) => update("tuev_until", e.target.value)} placeholder="06/2026" className="rounded-sm mt-1 font-mono-num" />
                                        </div>
                                        <div>
                                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Letzter Service</Label>
                                            <Input value={form.last_service} onChange={(e) => update("last_service", e.target.value)} placeholder="03/2025" className="rounded-sm mt-1 font-mono-num" />
                                        </div>
                                        <label className="flex items-center gap-2 cursor-pointer col-span-2">
                                            <input type="checkbox" checked={form.accident_free} onChange={(e) => update("accident_free", e.target.checked)} className="w-4 h-4 accent-[#FFCC00]" />
                                            <span className="text-sm">Unfallfrei</span>
                                        </label>
                                        <div className="col-span-2">
                                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Schäden / Mängel</Label>
                                            <Textarea value={form.defects} onChange={(e) => update("defects", e.target.value)} placeholder="Optional: kleine Kratzer, etc." rows={2} className="rounded-sm mt-1" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-100">
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Typ</Label>
                                    <Input value={form.bike_type} onChange={(e) => update("bike_type", e.target.value)} placeholder="MTB, E-Bike, Rennrad …" className="rounded-sm mt-1" />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Rahmengröße</Label>
                                    <Input value={form.frame_size} onChange={(e) => update("frame_size", e.target.value)} placeholder="M, 54 cm …" className="rounded-sm mt-1" />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Zustand (1=Top, 5=defekt)</Label>
                                    <Input type="number" min="1" max="5" value={form.condition_grade} onChange={(e) => update("condition_grade", e.target.value)} className="rounded-sm mt-1 font-mono-num" />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Akku (Wh, nur E-Bike)</Label>
                                    <Input type="number" value={form.battery_wh} onChange={(e) => update("battery_wh", e.target.value)} placeholder="500" className="rounded-sm mt-1 font-mono-num" />
                                </div>
                            </div>
                        )}

                        <div className="pt-4 border-t border-gray-100">
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Wunschpreis (optional, €)</Label>
                            <Input type="number" value={form.desired_price} onChange={(e) => update("desired_price", e.target.value)} placeholder="z. B. 8000" className="rounded-sm mt-1 font-mono-num" data-testid="input-desired-price" />
                            <p className="text-[11px] text-gray-500 mt-1">Transparenz hilft beiden Seiten — Händler geben oft passendere Angebote ab, wenn Ihre Vorstellung bekannt ist.</p>
                        </div>

                        <div className="flex justify-end pt-4">
                            <Button onClick={() => setStep(2)} disabled={!canNextStep1} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider px-8" data-testid="btn-next-step1">
                                {t("buy_request.btn_next_photos")}
                            </Button>
                        </div>
                    </div>
                )}

                {/* STEP 2 — Photos */}
                {step === 2 && (
                    <div className="bg-white border border-gray-200 rounded-sm p-6 space-y-4" data-testid="step-photos">
                        <h2 className="font-display text-2xl font-black">{t("buy_request.photos_title")}</h2>
                        <p className="text-sm text-gray-600">{t("buy_request.photos_hint")}</p>

                        <div className="grid grid-cols-3 gap-3">
                            {REQUIRED_IMG.map((r) => (
                                <ImgSlot key={r.key} keyName={r.key} label={r.label} required value={form[r.key]} uploading={uploading === r.key} onUpload={uploadImage} onClear={() => update(r.key, "")} />
                            ))}
                        </div>

                        <h3 className="font-bold text-sm uppercase tracking-wider text-gray-500 mt-6 pt-4 border-t border-gray-100">{t("buy_request.photos_optional")}</h3>
                        <div className="grid grid-cols-4 gap-3">
                            {OPTIONAL_IMG.map((r) => (
                                <ImgSlot key={r.key} keyName={r.key} label={r.label} value={form[r.key]} uploading={uploading === r.key} onUpload={uploadImage} onClear={() => update(r.key, "")} />
                            ))}
                        </div>

                        <div className="flex justify-between pt-4">
                            <Button onClick={() => setStep(1)} variant="outline" className="rounded-sm font-bold" data-testid="btn-back-step2">← {t("common.back")}</Button>
                            <Button onClick={() => setStep(3)} disabled={!canNextStep2} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider px-8" data-testid="btn-next-step2">
                                {t("buy_request.btn_next_contact")}
                            </Button>
                        </div>
                    </div>
                )}

                {/* STEP 3 — Contact */}
                {step === 3 && (
                    <div className="bg-white border border-gray-200 rounded-sm p-6 space-y-4" data-testid="step-contact">
                        <h2 className="font-display text-2xl font-black">{t("buy_request.contact_title")}</h2>
                        <p className="text-sm text-gray-600">{t("buy_request.contact_hint")}</p>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t("buy_request.name")} *</Label>
                                <Input value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} placeholder="Max Mustermann" className="rounded-sm mt-1" data-testid="input-name" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t("buy_request.email")} *</Label>
                                <Input type="email" value={form.contact_email} onChange={(e) => update("contact_email", e.target.value)} placeholder="ihre@email.de" className="rounded-sm mt-1" data-testid="input-email" />
                            </div>
                            <div className="col-span-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t("buy_request.phone_optional")}</Label>
                                <Input value={form.contact_phone} onChange={(e) => update("contact_phone", e.target.value)} placeholder="+49 …" className="rounded-sm mt-1" data-testid="input-phone" />
                                <p className="text-[11px] text-gray-500 mt-1">{t("buy_request.phone_hint")}</p>
                            </div>
                            <div className="col-span-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t("buy_request.message_optional")}</Label>
                                <Textarea value={form.message} onChange={(e) => update("message", e.target.value)} rows={3} placeholder="" className="rounded-sm mt-1" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t("buy_request.earliest_sale")}</Label>
                                <Input type="date" value={form.available_from} onChange={(e) => update("available_from", e.target.value)} className="rounded-sm mt-1 font-mono-num" />
                            </div>
                        </div>

                        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3 rounded-sm">
                            {t("buy_request.consent")}
                        </div>

                        <div className="flex justify-between pt-4">
                            <Button onClick={() => setStep(2)} variant="outline" className="rounded-sm font-bold" data-testid="btn-back-step3">← {t("common.back")}</Button>
                            <Button onClick={submit} disabled={!canSubmit || submitting} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold rounded-sm uppercase tracking-wider px-8" data-testid="btn-submit">
                                {submitting ? t("buy_request.sending") : <><ShoppingBag className="w-4 h-4 mr-2" /> {t("buy_request.btn_submit")}</>}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ImgSlot({ keyName, label, required, value, uploading, onUpload, onClear }) {
    return (
        <label className={`relative aspect-square border-2 border-dashed rounded-sm flex items-center justify-center cursor-pointer overflow-hidden ${value ? "border-[#FFCC00]" : required ? "border-[#DC2626]" : "border-gray-300"} hover:bg-gray-50`} data-testid={`img-slot-${keyName}`}>
            {value ? (
                <>
                    <img src={value} alt={label} className="w-full h-full object-cover" />
                    <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); onClear(); }}
                        className="absolute top-1 right-1 bg-white/90 rounded-full p-1 hover:bg-white"
                        data-testid={`img-clear-${keyName}`}
                    >
                        <X className="w-3 h-3" />
                    </button>
                </>
            ) : (
                <div className="text-center px-2">
                    {uploading ? (
                        <div className="text-xs text-gray-500">Lädt…</div>
                    ) : (
                        <>
                            <Upload className="w-5 h-5 mx-auto text-gray-400 mb-1" />
                            <div className="text-[11px] font-bold text-gray-700">{label}</div>
                            {required && <div className="text-[10px] text-[#DC2626] mt-0.5">Pflicht</div>}
                        </>
                    )}
                </div>
            )}
            <input type="file" accept="image/*" className="sr-only" onChange={(e) => onUpload(keyName, e.target.files?.[0])} />
        </label>
    );
}
